import { Box, Card, Typography } from '@mui/material';
import { capitalize } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Info } from '@phosphor-icons/react';
import { useState, useEffect, useRef } from 'react';

import { DependentPictureStyled } from '../FamilyDetailsPageStyled';
import { FamilyDrawerDisplayPicture } from '../FamilyDrawerDisplayPicture';
import {
  hairColorPalette,
  shirtColorPalette,
  skinColorPalette,
} from 'enrollment/app/constants';
import { ViewFamilyMemberDrawer } from '../ViewFamilyMemberDrawer';
import { TooltipWrapper } from 'enrollment/components/Shared/TooltipWrapper';
import './FamilyMemberCard.css';

export interface FamilyMemberCardProps {
  dependent?: any;
  showViewDetails?: boolean;
  children?: JSX.Element;
  dependentsData?: any;
}

export const displayDependentAvatar = (
  dependentAvatar: any,
  size?: number,
  altText?: string
) => {
  const avatar = dependentAvatar?.avatar ?? '';
  const profilePicture = dependentAvatar?.profile_picture ?? '';

  if (profilePicture) {
    const pictureSize = size ? size : 115.59;

    return (
      <Box
        component="img"
        src={profilePicture}
        alt={altText || ''}
        className="proile-picture"
        sx={{
          width: `${pictureSize}px`,
          borderRadius: '60px',
        }}
        aria-hidden="true"
        role="presentation"
      ></Box>
    );
  }

  const avatarName = avatar?.split('_');
  const avatarSkincolor = skinColorPalette.filter(
    (item) => avatarName && avatarName[1] === item.name
  );
  const avatarHairColor = hairColorPalette.filter(
    (item) => avatarName && avatarName[2] === item.name
  );
  const avatarShirtColor = shirtColorPalette.filter(
    (item) => avatarName && avatarName[3] === item.name
  );

  return (
    <FamilyDrawerDisplayPicture
      saveAvatarName={avatarName === undefined ? '' : avatarName[0]}
      saveAvatarSkinTone={avatarSkincolor[0]?.colorCode}
      saveAvatarHairColor={avatarHairColor[0]?.colorCode}
      saveAvatarShirtColor={avatarShirtColor[0]?.colorCode}
      size={size}
    />
  );
};

export const FamilyMemberCard = ({
  dependent,
  dependentsData,
  showViewDetails = false,
  children,
}: FamilyMemberCardProps) => {
  const { t } = useTranslation('pages.enrollmentFamily');
  const fullName = `${dependent?.first_name} ${dependent?.last_name}`;
  const firstName = dependent?.first_name;
  const altText = `${t('photo')} ${firstName}`;
  const nameId = `${dependent.id}-name`;
  const nameRef = useRef<HTMLElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (nameRef.current) {
        const { scrollHeight, clientHeight } = nameRef.current;
        setIsTextTruncated(scrollHeight > clientHeight);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [fullName]);

  return (
    <div className="col-xs-4 col-md-6 mx-auto mx-lg-0 mb-3 col-lg-4 p-0 pr-sm-4">
      <Card
        className="text-center py-4 px-3 px-md-4 family-member-card"
        data-testid="enrollment-family-member-card"
      >
        <DependentPictureStyled>
          {displayDependentAvatar(
            dependent?.dependent_profile,
            undefined,
            altText
          )}
        </DependentPictureStyled>
        <div className="pt-2 display1 position-relative">
          {isTextTruncated ? (
            <TooltipWrapper
              position="top"
              tooltipContent={fullName}
              allowWrap={true}
              tooltipContentId={`${nameId}-tooltip`}
            >
              <button
                ref={nameRef}
                className="dep-name display1"
                id={nameId}
                role="button"
              >
                <div>{fullName}</div>
              </button>
            </TooltipWrapper>
          ) : (
            <div ref={nameRef} className="dep-name display1" id={nameId}>
              {fullName}
            </div>
          )}
        </div>
        <Typography variant="body1">
          {capitalize(dependent?.relationship_code)}
          {dependent?.qmcso && (
            <TooltipWrapper
              position="bottom"
              tooltipContent={t('common:qmcsoValidation')}
              allowWrap={true}
              customClass="top-0 end-0"
            >
              <button
                className="bg-white"
                role="button"
                data-testid="qmcso-info-button"
              >
                <Info className="mx-1 mb-1" size={20} />
                <span className="sr-only">{t('common:qmcsoValidation')}</span>
              </button>
            </TooltipWrapper>
          )}
        </Typography>
        <Typography variant="body1">
          {`Born ${moment(dependent?.birthdate).format('MM/DD/YYYY')}`}
        </Typography>
        {showViewDetails ? (
          <div className="pt-3">
            <ViewFamilyMemberDrawer
              dependentsData={dependentsData}
              data={dependent}
              dependentId={dependent.id}
            />
          </div>
        ) : (
          <></>
        )}
      </Card>
      {children}
    </div>
  );
};
